import React, { useState } from "react";
import { useDispatch } from "react-redux";
import DayJS from "react-dayjs";
import {
    Alert,
    Box,
    Button,
    CardContent,
    Grid,
    Typography,
} from "@mui/material";
import ConfirmDialog from "../../../Common/Dialog/ConfirmDialog";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { retakeModule } from "../../../../actions/tmsActions";

const DateText = ({ date }) =>
    !!date ? (
        <DayJS format="DD MMM YYYY">{date}</DayJS>
    ) : (
        <span className="sr-only">No data available</span>
    );

const ScoreText = ({ score }) => {
    return (
        <Typography variant="body2">
            <strong>Score</strong>{" "}
            {score !== null && score !== undefined ? score : "N/A"}
        </Typography>
    );
};

const PassMarkText = ({ passmark }) => {
    return (
        <Typography variant="body2">
            <strong>Pass mark</strong>{" "}
            {passmark !== null && passmark !== undefined && passmark !== 0
                ? passmark
                : "N/A"}
        </Typography>
    );
};

function ModuleEnrolmentDetails({ module, onRetake }) {
    const dispatch = useDispatch();
    const [dialogOpen, setDialogOpen] = useState(null);
    if (!module) {
        return null;
    }

    const enrolment = module.enrolment;
    if (!enrolment) {
        return null;
    }

    const enrolmentMethod =
        enrolment.method === "assigned" ? "Assigned" : "Enrolled";

    const retakeAction = () =>
        dispatch(retakeModule("C-TMS", module.id, onRetake));

    // const isMandatory = enrolment.isMandatory ? 'Yes' : 'No';
    // const canRetake = enrolment.canRetake ? 'Yes' : 'No';

    // let isCancelled = false;
    // let isWaiting = false;

    let showDetails = false;

    switch (enrolment.status) {
        // same as 'default'; no need for extra entry
        // case 'enrolled':
        //     return null;

        case "waiting":
            enrolment.message = (
                <Alert
                    severity="info"
                    icon={<InfoOutlinedIcon fontSize="medium" />}
                >
                    You have been added to the <strong>Waiting list</strong>
                </Alert>
            );
            // isWaiting = true;
            break;

        case "cancelled":
            enrolment.message = (
                <Alert
                    severity="warning"
                    icon={<WarningAmberIcon fontSize="medium" />}
                >
                    Enrolment has been <strong>Cancelled</strong>
                </Alert>
            );
            // isCancelled = true;
            break;

        case "completed":
            showDetails = true;

            enrolment.message = (
                <Alert
                    severity="info"
                    icon={<CheckCircleIcon fontSize="medium" />}
                >
                    <strong>Completed</strong>
                </Alert>
            );

            if (enrolment?.success === "passed") {
                enrolment.message = (
                    <Alert
                        severity="success"
                        icon={<CheckCircleIcon fontSize="medium" />}
                    >
                        <strong>Passed</strong>
                    </Alert>
                );
            } else if (enrolment?.success === "failed") {
                enrolment.message = (
                    <Alert
                        severity="error"
                        icon={<ErrorOutlineIcon fontSize="medium" />}
                    >
                        <strong>Failed</strong>
                    </Alert>
                );
            }

            break;

        default:
            return null;
    }

    return (
        <CardContent>
            <Box className="card-content">
                <Grid container spacing={4}>
                    {enrolment.message && (
                        <Grid item xs={12} sm={12} md={12}>
                            {enrolment.message}
                        </Grid>
                    )}

                    {showDetails && (
                        <Grid item xs={12} sm={12} md={6}>
                            <Grid container spacing={4}>
                                <Grid item xs={12}>
                                    <Typography variant="body2">
                                        <strong>{enrolmentMethod}</strong>&nbsp;
                                        <DateText date={enrolment.date} />
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Completed</strong>&nbsp;
                                        <DateText
                                            date={enrolment.dateCompleted}
                                        />
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                    {showDetails && (
                        <Grid item xs={12} sm={12} md={6}>
                            <Grid container spacing={4}>
                                <Grid item xs={12}>
                                    <PassMarkText
                                        passmark={
                                            module?.requirement?.passMark | null
                                        }
                                    />
                                    <ScoreText score={enrolment.score} />
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                    {!!enrolment.canRetake && (
                        <Grid item xs={12} sm={12} md={12}>
                            <Grid container spacing={4}>
                                <Grid item xs={12}>
                                    <Button
                                        aria-label="Retake"
                                        variant="contained"
                                        onClick={() => setDialogOpen(true)}
                                    >
                                        Retake
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Box>
            {!!dialogOpen && (
                <ConfirmDialog
                    open={true}
                    title="Retake module"
                    content={`Retake module ${module.name}?`}
                    confirmButtonLabel="Retake"
                    onConfirm={retakeAction}
                    onCancel={() => setDialogOpen(false)}
                    onClose={() => setDialogOpen(false)}
                />
            )}
        </CardContent>
    );
}

export default ModuleEnrolmentDetails;
